// DEPTH.md §1–2: levels, beats, sections and the structure JSON (0-based, inclusive; same field names as the apps).

export enum Level { Outline = -2, Gist = -1, Original = 0, Explained = 1 }

export const LEVELS: Level[] = [Level.Outline, Level.Gist, Level.Original, Level.Explained];

export function levelName(l: Level): string {
  return { [-2]: "Outline", [-1]: "Gist", [0]: "Original", [1]: "Explained" }[l as -2 | -1 | 0 | 1];
}

export function levelSource(l: Level): string {
  return l === Level.Original ? "Author" : l === Level.Explained ? "Author + AI notes" : "AI summary";
}

export function clampLevel(n: number): Level {
  return Math.max(-2, Math.min(1, Math.round(n))) as Level;
}

export interface Beat { from: number; to: number; gist: string }
export interface Section { label: string; summary: string; firstBeat: number; lastBeat: number }
export interface Structure { kind: string; beats: Beat[]; sections: Section[]; summary: string }

export const isFiction = (s: Structure) => s.kind !== "nonfiction";

export function beatIndex(s: Structure, paragraph: number): number | null {
  const i = s.beats.findIndex((b) => b.from <= paragraph && paragraph <= b.to);
  return i >= 0 ? i : s.beats.length ? s.beats.length - 1 : null;
}

export function sectionIndex(s: Structure, beat: number): number | null {
  const i = s.sections.findIndex((x) => x.firstBeat <= beat && beat <= x.lastBeat);
  return i >= 0 ? i : null;
}

/** Paragraph range [lo, hi] of a section. */
export function sectionParagraphs(s: Structure, i: number): [number, number] {
  const sec = s.sections[i];
  return [s.beats[sec.firstBeat].from, s.beats[sec.lastBeat].to];
}

/** True when the beats cover exactly `count` paragraphs. */
export function fits(s: Structure | null | undefined, count: number): boolean {
  if (!s || !s.beats.length) return false;
  return s.beats[0].from === 0 && s.beats[s.beats.length - 1].to === count - 1;
}

export type AnchorKey = { kind: "paragraph" | "beat" | "section"; index: number };

export function anchorKeyFor(s: Structure | null, p: number, level: Level): AnchorKey | null {
  if (level >= 0) return { kind: "paragraph", index: p };
  if (!s) return null;
  const b = beatIndex(s, p);
  if (b === null) return null;
  if (level === Level.Gist) return { kind: "beat", index: b };
  const sec = sectionIndex(s, b);
  return sec === null ? null : { kind: "section", index: sec };
}

export function paragraphsOf(s: Structure | null, key: AnchorKey): [number, number] | null {
  if (key.kind === "paragraph") return [key.index, key.index];
  if (!s) return null;
  if (key.kind === "beat") return s.beats[key.index] ? [s.beats[key.index].from, s.beats[key.index].to] : null;
  return s.sections[key.index] ? sectionParagraphs(s, key.index) : null;
}
